import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PackageDetailsIcon from "../../assets/icons/PackageDetailsIcon.jsx";
import SharePackageDetailsModal from "./SharePackageDetailsModal.jsx";
import TimeSlotSelector from "./TimeSlotSelector";
import { selectCateringCartFilters, selectCateringCartPackages, selectCateringPackages } from "../../redux/features/restaurants/restaurantSelectors";
import { setCateringCartFilters } from "../../redux/features/restaurants/restaurantSlice";
import { getTodayDate, isDateBlockedForPackages } from "../../utils/timeSlots";

const FilterBar = () => {
  const dispatch = useDispatch();
  const [showSharePackageDetailsModal, setShowSharePackageDetailsModal] = useState(false);
  
  // Get filters from Redux state
  const filters = useSelector(selectCateringCartFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const cateringPackages = useSelector(selectCateringPackages);
  const today = getTodayDate();

  // Map catering packages to UI format for filtering
  const mapCateringPackagesToUIFormat = (apiPackages) => {
    if (!apiPackages || !Array.isArray(apiPackages)) return [];

    return apiPackages.map(pkg => ({
      id: pkg.id,
      restaurant_id: pkg.restaurant_id,
      cuttOffTimeInHours: pkg.cutt_off_time_in_hours,
      unavailabilitySchedule: pkg.unavailability_schedule || [],
    }));
  };

  // Get processed packages from Redux data for filtering
  const packages = mapCateringPackagesToUIFormat(cateringPackages);

  const handleOpenSharePackageDetailsModal = () => {
    setShowSharePackageDetailsModal(true);
  };

  const handleCloseSharePackageDetailsModal = () => {
    setShowSharePackageDetailsModal(false);
  };

  // Handle filter changes and dispatch to Redux
  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    
    // Check if the selected date is blocked by package constraints
    if (isDateBlockedForPackages(selectedDate, today, packages)) {
      // Check if it's due to cutoff time or unavailability
      const blockedPackages = packages.filter(pkg => {
        // Check cutoff time first
        if (pkg.cuttOffTimeInHours) {
          const cutoffHours = parseInt(pkg.cuttOffTimeInHours.match(/(\d+)/)?.[1] || '0');
          const selectedDateObj = new Date(selectedDate + 'T00:00:00');
          const todayObj = new Date(today + 'T00:00:00');
          const diffTime = selectedDateObj.getTime() - todayObj.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays === 0) {
            const now = new Date();
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            const hoursFromNow = 24 - currentHour - (currentMinute / 60);
            if (hoursFromNow < cutoffHours) {
              return true;
            }
          }
        }
        
        // Check unavailability schedule
        if (pkg.unavailabilitySchedule && pkg.unavailabilitySchedule.length > 0) {
          const dateObj = new Date(selectedDate);
          const formattedDate = `${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}-${dateObj.getFullYear()}`;
          
          const unavailabilityEntry = pkg.unavailabilitySchedule.find(entry => 
            entry.unavailable_date === formattedDate
          );
          
          if (unavailabilityEntry && unavailabilityEntry.unavailable_all_day) {
            return true;
          }
        }
        
        return false;
      });
      
      if (blockedPackages.length > 0) {
        // Check if it's due to cutoff time
        const cutoffBlocked = blockedPackages.some(pkg => pkg.cuttOffTimeInHours);
        if (cutoffBlocked) {
          const cutoffHours = parseInt(blockedPackages.find(pkg => pkg.cuttOffTimeInHours)?.cuttOffTimeInHours?.match(/(\d+)/)?.[1] || '0');
          if (selectedDate === today) {
            alert(`This date is not available for booking. Orders must be placed at least ${cutoffHours} hours in advance.`);
          } else {
            alert('This date is not available for booking due to cutoff time requirements.');
          }
        } else {
          alert('This date is not available for booking due to unavailability schedule.');
        }
        return;
      }
    }
    
    // Clear time selection when date changes to ensure valid time slot selection
    dispatch(setCateringCartFilters({ ...filters, date: selectedDate, time: "" }));
  };


  return (
    <div className="flex items-center gap-6 px-6 py-4 bg-white border-b border-gray-200">
      <div className="flex items-center gap-4">
        <span className="font-bold text-lg text-gray-400">Date</span>
        <input
          type="date"
          className="border border-gray-300 rounded-lg px-3 py-2 text-gray-500 font-bold w-40"
          value={filters.date}
          onChange={handleDateChange}
          min={today}
        />
      </div>
      
      <TimeSlotSelector />
      
      <div className="ml-auto flex items-center gap-4">
        <button
          onClick={handleOpenSharePackageDetailsModal}
          className="px-4 py-2 bg-[#13305D] text-white rounded-md shadow-sm hover:bg-[#0F294E] transition-colors flex items-center gap-2"
        >
           Package Details       <PackageDetailsIcon className="w-4 h-4" /> </button>
      </div>
      {showSharePackageDetailsModal && <SharePackageDetailsModal onClose={handleCloseSharePackageDetailsModal} />}
    </div>
  );
};

export default FilterBar; 